import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { casinos } from '../data/casinos';
import CasinoCard from '../components/sections/CasinoCard';
import PageHero from '../components/ui/PageHero';
import './CasinoGuide.css';

const guideTopics = [
  {
    title: 'Licens og regulering',
    icon: '🏛️',
    content: 'En dansk licens fra Spillemyndigheden er det absolutte minimumskrav. Casinoer uden licens er ulovlige i Danmark og tilbyder ingen forbrugerbeskyttelse. Tjek altid Spillemyndighedens offentlige register.'
  },
  {
    title: 'Spiludvalg',
    icon: '🎰',
    content: 'Kig på antallet af slots, live casino-spil og bordspil. Anerkendte softwareleverandører som Evolution, NetEnt og Pragmatic Play er et godt tegn på kvalitet. Mobiloplevelsen bør være på niveau med desktop.'
  },
  {
    title: 'Bonusser og vilkår',
    icon: '🎁',
    content: 'Velkomstbonusser kan se attraktive ud, men omsætningskrav, tidsfrister og maksimale gevinster kan gøre dem svære at udnytte. Læs altid det med småt, inden du accepterer en bonus.'
  },
  {
    title: 'Betalinger og udbetalinger',
    icon: '💳',
    content: 'Tjek, om casinoet understøtter danske betalingsmetoder som MobilePay, Dankort, bankoverførsel eller e-wallets. Udbetalingstider varierer typisk fra få timer til 5 arbejdsdage.'
  },
  {
    title: 'Kundesupport',
    icon: '💬',
    content: 'Tilgængelig, kompetent support på dansk er en stor fordel. Live chat med hurtige svartider er ideelt. Tjek åbningstider og om support er tilgængelig på din foretrukne kanal.'
  },
  {
    title: 'Ansvarligt spil',
    icon: '🛡️',
    content: 'Seriøse casinoer tilbyder indskudsgrænser, tidsgrænser, realitetschecks og selvudelukkelse. ROFUS-integration er lovpligtigt for alle licenserede operatører i Danmark.'
  },
];

const bonusTypes = [
  {
    type: 'Velkomstbonus',
    desc: 'En matchbonus på din første indbetaling – f.eks. 100% op til 1.000 kr. Næsten alle casinoer tilbyder dette.',
    pro: 'Giver dig ekstra kapital at spille for',
    con: 'Omsætningskrav kan være høje (f.eks. 35x)'
  },
  {
    type: 'Gratisspins',
    desc: 'Gratis spins på udvalgte spillemaskiner. Gevinsterne fra gratisspins er typisk underlagt omsætningskrav.',
    pro: 'Kan prøve spil uden risiko',
    con: 'Begrænset til specifikke spil'
  },
  {
    type: 'Cashback',
    desc: 'En procentdel af dine tab returneres – typisk ugentligt. Kan gives som kontanter eller bonuspenge.',
    pro: 'Reducerer det effektive tab',
    con: 'Kan have begrænsninger og omsætningskrav'
  },
  {
    type: 'Reload-bonus',
    desc: 'En matchbonus på efterfølgende indbetalinger. Typisk mindre generøs end velkomstbonussen.',
    pro: 'Belønner loyalitet',
    con: 'Tilgængelighed varierer'
  },
];

export default function CasinoGuide() {
  return (
    <>
      <Helmet>
        <title>Casino Guide – Sådan Vælger Du Det Bedste Online Casino | TopCasinoDansk</title>
        <meta name="description" content="Vores komplette guide til valg af online casino i Danmark. Lær hvad du skal kigge efter, og find det casino der passer til dig." />
      </Helmet>

      <PageHero
        title="Casino Guide: Vælg det rette casino"
        subtitle="Alt du behøver at vide om at vælge et licenseret, sikkert og underholdende online casino i Danmark."
        breadcrumb={<><Link to="/">Forside</Link> › Casino Guide</>}
      />

      {/* Guide intro */}
      <section className="guide-intro section">
        <div className="container">
          <div className="guide-intro-grid">
            <div>
              <div className="divider"></div>
              <h2>Hvad bør du kigge efter?</h2>
              <p>
                Med over 50 licenserede casinoer på det danske marked kan det være svært at finde det rigtige.
                Vores guide hjælper dig med at forstå de vigtigste faktorer, der adskiller et godt casino fra et dårligt.
              </p>
              <p>
                Husk: Det vigtigste kriterium er altid dansk licens. Et casino uden licens fra Spillemyndigheden
                er ulovligt i Danmark og tilbyder ingen beskyttelse af dine indsatser og personoplysninger.
              </p>
              <Link to="/vores-metode" className="btn btn-outline" style={{ marginTop: '0.5rem' }}>
                Se vores fulde anmeldelsesmetode
              </Link>
            </div>
            <div className="guide-checklist-box">
              <h4>Hurtig tjekliste</h4>
              <ul className="guide-quick-check">
                <li><span className="check-yes">✓</span> Gyldig dansk licens</li>
                <li><span className="check-yes">✓</span> ROFUS-integration</li>
                <li><span className="check-yes">✓</span> Tydelige vilkår og betingelser</li>
                <li><span className="check-yes">✓</span> Danske betalingsmetoder</li>
                <li><span className="check-yes">✓</span> Kundesupport på dansk</li>
                <li><span className="check-yes">✓</span> Ansvarlige spilredskaber</li>
                <li><span className="check-no">✗</span> Urealistiske bonustilbud</li>
                <li><span className="check-no">✗</span> Uklare udbetalingsvilkår</li>
                <li><span className="check-no">✗</span> Ingen kontaktmuligheder</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6 key factors */}
      <section className="guide-factors section bg-sand">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.5rem' }}>6 vigtige faktorer ved valg af casino</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Disse seks faktorer bør indgå i din evaluering af ethvert online casino.
          </p>

          <div className="factors-grid">
            {guideTopics.map((topic, i) => (
              <div key={i} className="factor-card">
                <div className="factor-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="factor-icon">{topic.icon}</div>
                <h3>{topic.title}</h3>
                <p>{topic.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonus types */}
      <section className="guide-bonuses section">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.5rem' }}>Forstå bonustyper</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Bonusser kan være værdifulde – men kun hvis du forstår vilkårene. Her er de mest almindelige typer.
          </p>

          <div className="bonus-types-grid">
            {bonusTypes.map((bonus, i) => (
              <div key={i} className="bonus-type-card">
                <h4>{bonus.type}</h4>
                <p className="bonus-desc">{bonus.desc}</p>
                <div className="bonus-pros-cons">
                  <div className="bonus-pro">
                    <span className="pro-icon">+</span>
                    <span>{bonus.pro}</span>
                  </div>
                  <div className="bonus-con">
                    <span className="con-icon">−</span>
                    <span>{bonus.con}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bonus-warning">
            <span className="bonus-warning-icon">⚠️</span>
            <p>
              <strong>Husk:</strong> En bonus med høje omsætningskrav (over 35x) er sjældent værd at acceptere.
              Læs altid vilkårene grundigt og spil ansvarligt. Bonus-jagt bør aldrig føre til uforsvarlige indskud.
            </p>
          </div>
        </div>
      </section>

      {/* Casino Showcase */}
      <section className="guide-showcase section bg-sand">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.5rem' }}>Vores anmeldte casinoer</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Alle casinoer nedenfor er verificeret med dansk licens og gennemgået af vores redaktion.
          </p>

          <div className="guide-casino-grid">
            {casinos.map((casino) => (
              <CasinoCard key={casino.id} casino={casino} variant="standard" />
            ))}
          </div>

          <p className="guide-casino-disclaimer">
            18+ · Spil ansvarligt · Alle casinoer er licenseret af Spillemyndigheden
          </p>
        </div>
      </section>

      {/* Safety reminder */}
      <section className="guide-safety-reminder section-sm">
        <div className="container">
          <div className="safety-reminder-bar">
            <div className="safety-reminder-text">
              <h4>🛡️ Husk: Ansvarligt spil er din ret</h4>
              <p>
                Du har altid ret til at sætte grænser, tage pauser og udelukke dig fra spil. Alle licenserede
                casinoer er lovpligtige til at respektere dine valg. Brug ROFUS, hvis du vil stoppe.
              </p>
            </div>
            <div className="safety-reminder-actions">
              <a href="https://www.rofus.nu/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                ROFUS.nu
              </a>
              <Link to="/ansvarligt-spil" className="btn btn-outline">
                Ansvarligt spil guide
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
